'use client';

import { useState } from 'react';
import { Skeleton } from '@repo/ui/components/skeleton';
import { TableCell, TableRow } from '@repo/ui/components/table';
import { TableShell, TableShellToolbar } from '@repo/ui/components/table-shell';
import { Tabs, TabsList, TabsTrigger } from '@repo/ui/components/tabs';

export interface LensOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Shared hover scope for a donut + ledger pair: pointing at a row highlights
 * the matching segment and the other way round. Preview only — the ledger
 * itself is the durable record, so there is nothing to pin.
 */
export function useLensSelection() {
  const [previewId, setPreviewId] = useState<string | null>(null);

  const rowProps = (id: string) => ({
    'data-state': previewId === id ? ('active' as const) : undefined,
    onMouseEnter: () => setPreviewId(id),
    onMouseLeave: () => setPreviewId(null)
  });

  return { previewId, setPreviewId, rowProps };
}

/** Hover-synced ledger row styling shared by every lens panel. */
export const LEDGER_ROW_CLASS =
  'hover:bg-rowa-surface-inset/60 data-[state=active]:bg-rowa-surface-inset transition-colors';

interface LensPanelProps<T extends string> {
  /** Optional anchor id so deep links can land on the panel. */
  id?: string;
  titleId: string;
  title: string;
  lenses: readonly LensOption<T>[];
  lens: T;
  onLensChange: (lens: T) => void;
  lensAriaLabel: string;
  children: React.ReactNode;
}

/**
 * Panel chrome for a lens-switched decomposition (Positions, ROWA asset
 * exposure): glass table shell, a single title-and-lens line, no ruled
 * frame. One reconciled population; the lens only reclassifies it.
 */
export function LensPanel<T extends string>({
  id,
  titleId,
  title,
  lenses,
  lens,
  onLensChange,
  lensAriaLabel,
  children
}: LensPanelProps<T>) {
  return (
    <TableShell
      id={id}
      role="region"
      aria-labelledby={titleId}
      className="flex scroll-mt-24 flex-col"
    >
      <TableShellToolbar className="border-b-0">
        <h2 id={titleId} className="text-rowa-card text-rowa-text-primary">
          {title}
        </h2>

        <Tabs value={lens} onValueChange={(value) => onLensChange(value as T)}>
          <TabsList variant="segmented" aria-label={lensAriaLabel}>
            {lenses.map((item) => (
              <TabsTrigger key={item.value} value={item.value}>
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </TableShellToolbar>

      {children}
    </TableShell>
  );
}

/** The donut-and-ledger layout every lens panel shares. */
export function LensPanelBody({
  donut,
  children
}: {
  donut: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    // Sized by the card, not the viewport: the donut sits beside the ledger
    // from 40rem of card width — low enough that a half-width panel on a
    // 1440px screen qualifies — and stacks on top only on narrow screens.
    // No ruled frame around either; the chart floats in clear space.
    <div className="@container">
      {/* The ledger leads: the chart column stops at 18rem so the table's
          every column fits without scrolling sideways (at 28rem the share
          column was cut off on a half-width panel), and the ring still
          reads at a glance. */}
      <div className="grid @min-[40rem]:grid-cols-[minmax(13rem,18rem)_minmax(0,1fr)]">
        <div className="flex items-center p-5">{donut}</div>
        {/* border-none beats the table primitives' rules by style, not
            specificity: the ledger separates rows with spacing and hover
            tint alone. */}
        <div className="min-w-0 overflow-x-auto [&_tr]:border-none">
          {children}
        </div>
      </div>
    </div>
  );
}

/** Bottom scope note + action row. */
export function LensPanelFooter({
  note,
  action
}: {
  note: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    // mt-auto: the shell is a flex column, so when two panels share a grid
    // row the shorter card's footer still sits on its bottom edge.
    <div className="border-rowa-border-default text-rowa-label-sm text-rowa-text-tertiary mt-auto flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3">
      <span>{note}</span>
      {action}
    </div>
  );
}

export function LedgerLoadingRows({
  rows = 5,
  columns
}: {
  rows?: number;
  columns: number;
}) {
  return Array.from({ length: rows }).map((_, rowIndex) => (
    <TableRow key={rowIndex}>
      {Array.from({ length: columns }).map((__, cellIndex) => (
        <TableCell key={cellIndex} className="py-4">
          <Skeleton className="h-5 w-full" />
        </TableCell>
      ))}
    </TableRow>
  ));
}
